import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowRight } from 'lucide-react';

export default function Craftsmanship() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const processSteps = [
    {
      number: '01',
      title: 'Valg og Tilberedelse av Tre',
      description: 'Vi velger C24-tre fra sertifiserte nordiske dostawcy. Tørket og sertifisert tre av høy kvalitet sikrer at vi kan bygge solide badstuer. Våre dostawcy garanterer konsistent kvalitet, så vi vet at hver komponent er pålitelig. Treet tørkes under kontrollerte forhold, deretter kuttes presist med avanserte CNC-maskiner for millimeterpresisjon på hver element.',
      image: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/5f5c2692b_pexels-aleks-bm-3474052-7744521.jpg'
    },
    {
      number: '02',
      title: 'Konstruksjon av Struktur',
      description: 'Vi bygger en solid ramme av konstruksjonstré, forsterket og testet for stabilitet. Hver forbindelse er presist utført og testet.',
      image: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/511882600_KonstruksjonC24.png'
    },
    {
      number: '03',
      title: 'Kledning og Isolasjon',
      description: 'Innvendig monterer vi høykvalitets termisolasjon. Innvendige vegger bekleses med asp eller furu, som er sikre ved høy temperatur.',
      image: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/c4edb10f1_448562757_1189861288860504_6885993724912014667_n-1-qpzgwovhew35ezb8d4ocs54eyo4qqwh7tgga36hrq8.jpg'
    },
    {
      number: '04',
      title: 'Montering av Benker og Dekorasjon',
      description: 'Vi installerer benker, hyller og alle innvendige elementer. Hver komponent justeres manuelt og finisjes til perfektion.',
      image: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/d9a406423_Benkerogdekorasjon.jpg'
    },
    {
      number: '05',
      title: 'Badstupanel',
      description: 'Vi monterer badstupaneler av premium-materiale. Vi bruker Thermo Aspen for mildt og komfortabelt interiør, Or for klassisk skandinavisk look, eller Abachi for eksklusiv finisj. Hvert panel kuttes presist og monteres med håndverk for perfekt passform.',
      image: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/8846f8bcb_Monteringavpanel.jpg'
    },
    {
      number: '06',
      title: 'Installasjon av Varmesystem',
      description: 'Vi monterer ovnen, ventilasjonssystemet og alle elektriske komponenter. Hvert system testes individuelt før montasje i badstuen.',
      image: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/9daa6befa_pexels-huum-sauna-heaters-718199222-31092908.jpg'
    },
    {
      number: '07',
      title: 'Tester og Kvalitetskontroll',
      description: 'Hver badstue gjennomgår strenge tester — fra tetthet til varmesystemets ytelse. Alt dokumenteres og godkjennes.',
      image: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/6b5bd61e4_Testbadstue.png'
    },
    {
      number: '08',
      title: 'Ferdig Enhet',
      description: 'Ferdig enhet er klar for transport fra fabrikken. Vi sikrer den med plastfilm for å beskytte den under frakt og levering til ditt hjem.',
      image: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/dc5968401_d11f2725-ed8b-4205-8054-6ffbe165e9c3.jpg'
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative h-[600px] mb-0 overflow-hidden"
      >
        <img
          src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/c4edb10f1_448562757_1189861288860504_6885993724912014667_n-1-qpzgwovhew35ezb8d4ocs54eyo4qqwh7tgga36hrq8.jpg"
          alt="Prosess med å bygge badstuer"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-lg tracking-widest-2xl font-body uppercase text-white/60 mb-6">Prosess</p>
            <h1 className="font-heading text-6xl md:text-7xl lg:text-8xl font-light leading-[1.0] text-white mb-6">
              Håndverk
            </h1>
            <p className="font-body text-white/80 text-2xl max-w-2xl leading-relaxed">
              Fra valg av tre til ferdig badstue — hvert trinn utført med nordisk presisjon og dedikasjon
            </p>
          </motion.div>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-6 md:px-12">

        {/* Intro Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-20"
        >
          <div className="max-w-4xl mx-auto text-center mb-20">
            <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-6">Filosofi</p>
            <h2 className="font-heading text-5xl md:text-6xl font-light leading-[1.2] mb-8 text-[#2a1810]">
              Hver detalj <span className="italic">betyr noe</span>
            </h2>
            <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-6">
              Å bygge en LYKKE-badstue er en prosess som varer måneder. Vi forkorter ikke trinnene. Vi bruker ikke billige materialer. Hver komponent velges, utføres og testes med absolutt dedikasjon.
            </p>
            <p className="font-body text-2xl text-foreground/70 leading-relaxed">
              Dette er ikke masseproduks­jon — dette er håndverk. Hver badstue er en historie om tre, om prosess, om nordiske tradisjoner.
            </p>
          </div>
        </motion.section>

        {/* Process Steps */}
        <div className="py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-4">Byggeprosess</p>
            <h2 className="font-heading text-5xl font-light leading-[1.2] text-[#2a1810]">
              9 Trinn fra Skog til Hjem
            </h2>
          </motion.div>

          <div className="space-y-24">
            {processSteps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.05 }}
                className={`grid md:grid-cols-2 gap-16 items-center ${idx % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
              >
                <div className={idx % 2 === 1 ? 'md:order-2' : ''}>
                  <div className="mb-6">
                    <span className="font-heading text-6xl font-light text-[#2d5a3d]/30">{step.number}</span>
                  </div>
                  <h3 className="font-heading text-4xl font-light mb-6 text-[#2a1810]">
                    {step.title}
                  </h3>
                  <p className="font-body text-2xl text-foreground/70 leading-relaxed">
                    {step.description}
                  </p>
                </div>
                <div className={idx % 2 === 1 ? 'md:order-1' : ''}>
                  <div className="rounded-[8px] overflow-hidden h-96 shadow-lg">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Materialer Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-20 border-t border-border/20"
        >
          <div className="mb-16">
            <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-4">Materialer</p>
            <h2 className="font-heading text-5xl font-light leading-[1.2] text-[#2a1810]">
              Kun de Beste <span className="italic">Materialene</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {[
              {
                title: 'Nordisk Tre',
                desc: 'Furu og gran fra nordlige skoger — holdbar, vakker og naturlig. Hvert tre velges for kvaliteten på ringene.',
                features: ['Naturlig holdbarhet', 'Vakker tekstur', 'Fuktighetsregulering']
              },
              {
                title: 'Asp',
                desc: 'For badstueinteriøret — trygt ved høy temperatur, brenner ikke og avgir ingen utsendelser. Eneste tre som fortjener interiøret.',
                features: ['Trygt ved 100°C+', 'Brenner ikke', 'Naturlig lyst']
              },
              {
                title: 'Kebony Kledning',
                desc: 'Modifisert norsk tre — kombinerer hardhet av eksotisk tre med bearbeidbarhet av nordisk furu. 30 års garanti.',
                features: ['30 års garanti', 'Naturlig aldring', 'Ingen vedlikehold']
              },
              {
                title: 'Varmesystemer',
                desc: 'Ovner fra anerkjente produsenter — Huum, Harvia eller lignende. Hvert system testes før montering.',
                features: ['Sertifisert', 'Energieffektiv', 'Holdbar']
              }
            ].map((material, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -8 }}
                className="bg-secondary/30 rounded-[8px] p-8 border border-border/20"
              >
                <h3 className="font-heading text-2xl font-light mb-4 text-[#2a1810]">
                  {material.title}
                </h3>
                <p className="font-body text-2xl text-foreground/70 mb-6 leading-relaxed">
                  {material.desc}
                </p>
                <ul className="space-y-2">
                  {material.features.map((feat, i) => (
                    <li key={i} className="flex gap-3 text-foreground/70 text-2xl">
                      <span className="text-primary font-light">•</span>
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Standarder */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-20 border-t border-border/20"
        >
          <div className="mb-16">
            <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-4">Standarder</p>
            <h2 className="font-heading text-5xl font-light leading-[1.2] text-[#2a1810]">
              Sertifisering & <span className="italic">Garantier</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { label: 'Norske Standarder', desc: 'NS-EN 1990, TEK17 — designer for nordiske ekstremer' },
              { label: '30 År Garanti', desc: 'På trestruktur og Kebony. Investering for generasjoner.' },
              { label: 'Full Dokumentasjon', desc: 'Hver badstue leveres med FDV og vedlikeholdsinstrukser.' },
              { label: 'Kvalitetskontroller', desc: 'To-trinns inspeksjon — i fabrikk og på stedet.' },
              { label: 'Livslang Support', desc: 'Teknisk støtte tilgjengelig gjennom hele livet.' },
              { label: 'Sertifiserte Materialer', desc: 'Alle elementer fra verifiserte, sertifiserte kilder.' }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -4 }}
                className="bg-white rounded-[8px] p-6 border border-border/20 hover:border-[#2d5a3d] transition-colors"
              >
                <h3 className="font-heading text-lg font-light mb-3 text-[#2a1810]">
                  {item.label}
                </h3>
                <p className="font-body text-2xl text-foreground/70">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-20 border-t border-border/20"
        >
          <div className="bg-gradient-to-br from-[#2a1810] to-[#3d2818] text-white rounded-[8px] p-12 md:p-20 text-center">
            <p className="text-sm tracking-widest-2xl font-body uppercase text-white/60 mb-6">Neste Steg</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.2] mb-6">
              Klar for Din Badstue?
            </h2>
            <p className="font-body text-2xl text-white/80 mb-12 max-w-2xl mx-auto">
              Nå vet du hvordan vi bygger. Oppdag våre kolleksjoner og konfigurer din ideelle badstue.
            </p>
            <motion.a
              whileHover={{ scale: 1.05 }}
              href="/sauna-ly"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#2a1810] font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-white/90 transition-all"
            >
              Oppdag Kolleksjoner <ArrowRight size={16} />
            </motion.a>
          </div>
        </motion.section>

      </div>

      <Footer />
    </div>
  );
}